import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, Calendar, Clock, Check, RefreshCw,
  Monitor, Users, BrainCircuit, Loader2
} from 'lucide-react';
import { useSubscription } from '../../context/SubscriptionContext';
import { subscriptionAPI } from '../../services/api';

const StatusBadge = ({ status }) => {
  const config = {
    active: { label: 'نشط', bg: 'bg-green-100 text-green-700' },
    trial: { label: 'تجريبي', bg: 'bg-amber-100 text-amber-700' },
    trialing: { label: 'تجريبي', bg: 'bg-amber-100 text-amber-700' },
    expired: { label: 'منتهي', bg: 'bg-red-100 text-red-700' },
    past_due: { label: 'متأخر بالدفع', bg: 'bg-red-100 text-red-700' },
    canceled: { label: 'ملغي', bg: 'bg-gray-100 text-gray-600' },
    inactive: { label: 'غير نشط', bg: 'bg-gray-100 text-gray-600' },
  };
  const c = config[status] || config.inactive;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${c.bg}`}>
      {c.label}
    </span>
  );
};

const UsageBar = ({ label, used, max, icon: Icon }) => {
  const isUnlimited = max === '∞' || max === -1;
  const pct = isUnlimited ? Math.min(30, used > 0 ? 15 : 0) : (max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-gray-700 font-medium">
          <Icon className="w-4 h-4 text-gray-400" />
          {label}
        </div>
        <span className="text-gray-500">{used} / {isUnlimited ? '∞' : max}</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isUnlimited ? 'bg-emerald-400' : pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const MySubscription = () => {
  const navigate = useNavigate();
  const { subscription, plan, features, limits, status, refreshSubscription } = useSubscription();
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [usageStats, setUsageStats] = useState(null);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  useEffect(() => {
    const loadData = async () => {
      setPaymentsLoading(true);
      try {
        const [paymentsRes, usageRes] = await Promise.all([
          subscriptionAPI.getPayments({ limit: 10 }),
          subscriptionAPI.getUsage(),
        ]);
        setPayments(paymentsRes.data?.data?.items || paymentsRes.data?.data || []);
        setUsageStats(usageRes.data?.data || null);
      } catch (e) {
        console.error('Failed to load data', e);
      } finally {
        setPaymentsLoading(false);
      }
    };
    loadData();
  }, []);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('ar-SA-u-ca-gregory', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // The API response nests subscription details under subscription.subscription
  const subDetails = subscription?.subscription || subscription;
  // Usage from dedicated endpoint
  const usedUsers = usageStats?.users?.current || 0;
  const maxUsers = usageStats?.users?.max || limits.maxUsers || 5;
  const usedDevices = usageStats?.devices?.current || 0;
  const maxDevices = usageStats?.devices?.unlimited ? -1 : ((usageStats?.devices?.max || limits.maxDevices || 3) + (usageStats?.devices?.additional || 0));
  const usedAI = usageStats?.aiAnalysis?.current || 0;
  const maxAI = usageStats?.aiAnalysis?.unlimited ? -1 : (usageStats?.aiAnalysis?.max || limits.maxAIAnalysisPerMonth || 0);

  const planName = plan?.nameAr || plan?.name || '-';
  const billingCycle = subDetails?.billingCycle === 'yearly' ? 'سنوي' : 'شهري';
  const planPrice = subDetails?.totalPrice || subDetails?.basePrice ||
    (subDetails?.billingCycle === 'yearly' ? plan?.yearlyPrice : plan?.monthlyPrice) || 0;

  // Prefer the admin-written highlight bullets (populated in the plan
  // management form as "نقاط المميزات"). The raw `features[]` keys are
  // internal feature flags — exposing them to end users surfaces every tiny
  // technical toggle (`lab_chat`, `multi_payment`, …) as if it were a
  // marketing feature, which is confusing and unprofessional. Fall back to
  // the technical list only if the plan has no highlights configured at all.
  const highlights = Array.isArray(plan?.highlightPoints) ? plan.highlightPoints : [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" dir="rtl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">اشتراكي</h1>
            <p className="text-gray-500 text-sm mt-1">إدارة اشتراكك وعرض تفاصيل الباقة</p>
          </div>
          <button
            onClick={() => navigate('/settings/subscription/renew')}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-lg shadow-emerald-200"
          >
            <RefreshCw className="w-4 h-4" />
            تجديد الاشتراك
          </button>
        </div>

        {/* Plan Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">{planName}</h2>
                <p className="text-gray-500 text-sm">
                  {planPrice > 0 ? `${planPrice} ر.س / ${billingCycle}` : 'مجاني'}
                </p>
              </div>
            </div>
            <StatusBadge status={status} />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <Calendar className="w-3.5 h-3.5" />
                بداية الاشتراك
              </div>
              <p className="text-sm font-semibold text-gray-700">
                {formatDate(subDetails?.startDate)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <Calendar className="w-3.5 h-3.5" />
                نهاية الاشتراك
              </div>
              <p className="text-sm font-semibold text-gray-700">
                {formatDate(subDetails?.endDate)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <Clock className="w-3.5 h-3.5" />
                نهاية التجربة
              </div>
              <p className="text-sm font-semibold text-gray-700">
                {formatDate(subDetails?.trialEndDate)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <CreditCard className="w-3.5 h-3.5" />
                نوع الدفع
              </div>
              <p className="text-sm font-semibold text-gray-700">
                {billingCycle}
              </p>
            </div>
          </div>

          {/* Resource Usage */}
          <div className="space-y-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700">استخدام الموارد</h3>
            <UsageBar label="المستخدمون" used={usedUsers} max={maxUsers === -1 ? '∞' : maxUsers} icon={Users} />
            <UsageBar label="الأجهزة المربوطة" used={usedDevices} max={maxDevices === -1 ? '∞' : maxDevices} icon={Monitor} />
            {maxAI !== 0 && (
              <UsageBar label="تحليلات AI هذا الشهر" used={usedAI} max={maxAI === -1 ? '∞' : maxAI} icon={BrainCircuit} />
            )}
          </div>
        </div>

        {/* Features — only render when the plan has admin-written highlights.
            No fallback to the technical feature-flag grid; it confused users
            by exposing internal booleans like `lab_chat` / `multi_payment`. */}
        {highlights.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">مميزات الباقة</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {highlights.map((h, i) => {
                const text = typeof h === 'string' ? h : (h?.text || h?.textEn || '');
                if (!text) return null;
                return (
                  <li key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-700">{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Payment History */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">سجل المدفوعات</h3>
          {paymentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : payments.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">لا توجد مدفوعات</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-right py-3 px-3 text-gray-500 font-medium">التاريخ</th>
                    <th className="text-right py-3 px-3 text-gray-500 font-medium">المبلغ</th>
                    <th className="text-right py-3 px-3 text-gray-500 font-medium">الحالة</th>
                    <th className="text-right py-3 px-3 text-gray-500 font-medium">الوصف</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={p._id || i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-3 text-gray-700">{formatDate(p.paymentDate || p.createdAt)}</td>
                      <td className="py-3 px-3 text-gray-700 font-medium">{p.amount} ر.س</td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.status === 'completed' ? 'bg-green-100 text-green-700' :
                          p.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          p.status === 'refunded' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {p.status === 'completed' ? 'مدفوع' : p.status === 'pending' ? 'معلق' : p.status === 'refunded' ? 'مسترد' : p.status === 'failed' ? 'فاشل' : p.status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-gray-500">{p.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default MySubscription;
